import React from 'react';
import {
  Badge, Box, Divider, IconButton, Menu, Tooltip, Typography,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Language as WebsiteIcon,
  Payment as CreditIcon,
  ConfirmationNumberOutlined as TicketIcon,
} from '@mui/icons-material';
import { NOTIFICATION_KINDS } from '../utils/notifications';

// The header bell. It previously rendered an icon with no click handler and no
// content — QA reasonably asked what it was for.
//
// Every row is ACTIONABLE: it names a condition and the one place that fixes
// it, and clicking navigates there. There is no "mark as read", because these
// are states rather than events — see utils/notifications.js for why that is
// deliberate rather than missing.

const ICONS = {
  [NOTIFICATION_KINDS.WEBSITE_DISCONNECTED]: WebsiteIcon,
  [NOTIFICATION_KINDS.CREDITS_EXPIRING]: CreditIcon,
  [NOTIFICATION_KINDS.TICKET_REPLY]: TicketIcon,
};

const TONES = {
  warning: { fg: '#A7391E', bg: 'rgba(167,57,30,0.10)' },
  info: { fg: '#1C7C73', bg: 'rgba(0,106,99,0.10)' },
};

const NotificationsMenu = ({ notifications = [], onOpen, onNavigate, textMuted = '#64748B' }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    // Re-read on open so acting on one notification updates the rest of the
    // list rather than leaving the panel showing a stale snapshot.
    if (onOpen) onOpen();
  };

  const handleGo = (path) => {
    setAnchorEl(null);
    if (onNavigate) onNavigate(path);
  };

  return (
    <>
      <Tooltip title="Notifications">
        {/* aria-label carries the count so it is not conveyed by the badge
            colour alone. */}
        <IconButton
          size="small"
          onClick={handleOpen}
          aria-label={notifications.length ? `Notifications: ${notifications.length}` : 'Notifications'}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <Badge
            badgeContent={notifications.length}
            color="error"
            overlap="circular"
            sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}
          >
            <NotificationsIcon sx={{ color: textMuted, fontSize: 22 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 360, maxWidth: '92vw', borderRadius: '14px', mt: 1 } } }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 15, color: '#0F172A' }}>
            Notifications
          </Typography>
        </Box>
        <Divider />

        {notifications.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: textMuted }}>
              You&apos;re all caught up.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 380, overflowY: 'auto', py: 0.5 }}>
            {notifications.map((n) => {
              const Icon = ICONS[n.kind] || NotificationsIcon;
              const tone = TONES[n.severity] || TONES.info;
              return (
                // A button, not a styled div: this navigates, so it has to be
                // reachable and activatable from the keyboard.
                <Box
                  key={n.id}
                  component="button"
                  type="button"
                  onClick={() => handleGo(n.path)}
                  sx={{
                    width: '100%', display: 'flex', alignItems: 'flex-start', gap: 1.5,
                    px: 2, py: 1.5, border: 'none', bgcolor: 'transparent', textAlign: 'left',
                    cursor: 'pointer', font: 'inherit',
                    '&:hover': { bgcolor: '#F8FAFA' },
                    '&:focus-visible': { outline: '2px solid #1C7C73', outlineOffset: '-2px' },
                  }}
                >
                  <Box sx={{
                    width: 30, height: 30, borderRadius: '8px', bgcolor: tone.bg, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', mt: '2px',
                  }}>
                    <Icon sx={{ color: tone.fg, fontSize: 16 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, lineHeight: '18px', color: '#0F172A' }}>
                      {n.message}
                    </Typography>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 12, color: '#1C7C73', mt: 0.25 }}>
                      {n.actionLabel}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Menu>
    </>
  );
};

export default NotificationsMenu;
